import React, { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ModalWrapper from "../Modal";
import { getErrorMessage } from "../../utils/getErrorMessage";
import useShippingCountries from "../../hooks/useShippingCountries";
import { createShippingRegion } from "../../services/shippingService";

const AddRegionModal = ({ isOpen, onClose, onSuccess }) => {
  const { countries } = useShippingCountries();
  const [formData, setFormData] = useState({
    shipping_country_id: "",
    region_name: "",
    shipping_fee: "",
    free_shipping_above: "",
  });

  const { mutate: handleCreate, isPending } = useMutation({
    mutationFn: () => createShippingRegion(formData),
    onSuccess: () => {
      toast.success("Region created successfully!");
      setFormData({
        shipping_country_id: "",
        region_name: "",
        shipping_fee: "",
        free_shipping_above: "",
      });
      onClose();
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create region."));
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (
      !formData.shipping_country_id ||
      !formData.region_name ||
      !formData.shipping_fee
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    handleCreate();
  };
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} hasPadding={false}>
      <div className="w-full max-w-[540px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#EEF1F3] pb-1 px-4 pt-3">
          <h2 className="text-xl font-medium text-[#0C0C0E]">Add Region</h2>
          <button
            onClick={onClose}
            className="text-[#4F535D] hover:text-[#6B7143] transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form Body */}
        <div className="space-y-6 px-4 py-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#383B41]">
              Country <span>*</span>
            </label>
            <div className="relative">
              <select
                name="shipping_country_id"
                value={formData.shipping_country_id}
                onChange={handleChange}
                className="w-full appearance-none rounded-xl border border-[#D9DBDE] bg-white px-4 py-3.5 text-[#0C0C0E] shadow-sm outline-none focus:border-gray-400 transition-all"
              >
                <option value="">Select a country</option>
                {countries &&
                  countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.country}
                    </option>
                  ))}
              </select>
              <ChevronDown
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D9DBDE] pointer-events-none"
                size={20}
              />
            </div>
          </div>

          {/* Region Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#383B41]">
              Region Name <span>*</span>
            </label>
            <input
              type="text"
              name="region_name"
              value={formData.region_name}
              onChange={handleChange}
              placeholder="e.g Lagos, California"
              className="w-full rounded-xl border border-[#D9DBDE] px-4 py-3.5 placeholder:text-gray-300 outline-none focus:border-gray-400 transition-all shadow-sm"
            />
          </div>

          {/* Shipping Fee */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#383B41]">
              Shipping Fee (₦) <span>*</span>
            </label>
            <input
              type="number"
              name="shipping_fee"
              value={formData.shipping_fee}
              onChange={handleChange}
              placeholder="0.0"
              className="w-full rounded-xl border border-[#D9DBDE] px-4 py-3.5 placeholder:text-gray-300 outline-none focus:border-gray-400 transition-all shadow-sm"
            />
          </div>

          {/* Free Shipping Threshold */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#383B41]">
              Free Shipping Above (₦) <span>*</span>
            </label>
            <input
              type="number"
              name="free_shipping_above"
              value={formData.free_shipping_above}
              onChange={handleChange}
              placeholder="0 for no free shipping"
              className="w-full rounded-xl border border-[#D9DBDE] px-4 py-3.5 placeholder:text-gray-300 outline-none focus:border-gray-400 transition-all shadow-sm"
            />
            <p className="text-xs text-[#7D828D] font-medium">
              Orders above this amount get free shipping. Set to 0 to disable.
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-4 px-4 py-3 border-t border-[#EEF1F3]">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl bg-gradient-to-r from-white to-[#F3F3F3] border border-[#D9DBDE] py-3.5 text-base font-medium text-[#4F535D] hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="flex-1 rounded-xl bg-[#D9C9B8] py-3.5 text-base font-medium text-gray-800 hover:bg-[#C8B8A7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default AddRegionModal;
